import { createFileRoute, useParams } from '@tanstack/react-router';
import { Card, Stack, Text, Title } from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { useQuery } from '@tanstack/react-query';
import { fetchList } from '@/shared/api/jsonapi/client';
import DOMPurify from 'dompurify';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { PageLoader } from '@/shared/components/LoadingState';

interface LegalTerm extends Denormalized {
  type: 'legal-terms';
  code?: string;
  title?: string;
  body?: string;
  language?: string;
  version?: string;
}

export const Route = createFileRoute('/_public/terms/$code')({ component: TermsPage });

function TermsPage() {
  const { code } = useParams({ from: '/_public/terms/$code' });
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['legal-terms', code],
    queryFn: () =>
      fetchList<LegalTerm>('legal-terms', {
        filter: { code: { eq: code } },
        page: { size: 1 },
      }),
  });

  const term = data?.data?.[0];

  if (isError) return <QueryErrorAlert error={error} onRetry={() => void refetch()} />;
  if (isLoading) return <PageLoader label="Carico il documento…" />;
  if (!term) {
    return (
      <Card withBorder padding="xl">
        <Title order={2}>Documento non trovato</Title>
        <Text c="dimmed">Il documento legale richiesto non esiste o non è attivo.</Text>
      </Card>
    );
  }

  return (
    <Card withBorder padding="xl" radius="md">
      <Stack>
        <Text component="h1" fw={700} fz="var(--fs-h1)" lh={1.1}>{term.title ?? code}</Text>
        {term.version && <Text size="sm" c="dimmed">Versione {term.version}</Text>}
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(term.body ?? '') }} />
      </Stack>
    </Card>
  );
}
